import _ from 'lodash';
import { ISurveyOption, ISurveyQuestion, QuestionType } from 'src/services/api/api-client';

const isContentQuestion = (questionType: QuestionType) =>
  questionType === QuestionType.Content || questionType === QuestionType.Gallery;

export type ErrorKeys = keyof ISurveyQuestion | keyof ISurveyOption;

export type ValidationRuleType = {
  key: ErrorKeys;
  text: string;
  validate: (question: ISurveyQuestion, optionIndex?: number) => boolean | undefined;
};

export const ValidationRules: { [key: string]: ValidationRuleType } = {
  OptionValueRequiredRule: {
    key: 'optionValue',
    text: 'Option value is required',
    validate: (editableQuestion: ISurveyQuestion, optionIndex?: number) => {
      if (isContentQuestion(editableQuestion.questionType)) {
        return false;
      }

      return (
        editableQuestion.options?.some(
          (option, index) => (optionIndex === undefined || index === optionIndex) && !option.optionValue?.trim(),
        ) ?? false
      );
    },
  },

  OptionValueRule: {
    key: 'optionValue',
    text: 'Option value should be unique (case-insensitive)',
    validate: (editableQuestion: ISurveyQuestion, optionIndex?: number) => {
      if (isContentQuestion(editableQuestion.questionType)) {
        return false;
      }
      const optionValues = editableQuestion?.options?.map((o) => o.optionValue?.toLowerCase()) ?? [];
      if (optionIndex !== undefined) {
        return optionValues.some(
          (optionValue, index) => index !== optionIndex && optionValue === optionValues[optionIndex],
        );
      }
      const uniq = _.uniq(optionValues).length;
      return optionValues.length !== uniq;
    },
  },

  OptionCountRule: {
    key: 'options',
    text: 'Question has no options',
    validate: (editableQuestion: ISurveyQuestion) => {
      if (isContentQuestion(editableQuestion.questionType)) {
        return false;
      }
      return !editableQuestion?.options?.length;
    },
  },

  ContentBlockCountRule: {
    key: 'options',
    text: 'Content question should have at least 1 block',
    validate: (editableQuestion: ISurveyQuestion) => {
      if (!isContentQuestion(editableQuestion.questionType)) {
        return false;
      }

      return !editableQuestion.contentBlock?.length;
    },
  },

  ContentOptionsEmptyRule: {
    key: 'options',
    text: 'Content and gallery questions must not contain answer options',
    validate: (editableQuestion: ISurveyQuestion) => {
      if (!isContentQuestion(editableQuestion.questionType)) {
        return false;
      }

      return (editableQuestion.options?.length ?? 0) > 0;
    },
  },

  ContentImageResourceKeyRule: {
    key: 'options',
    text: 'Image resource key is required when image is configured',
    validate: (editableQuestion: ISurveyQuestion) => {
      if (!isContentQuestion(editableQuestion.questionType)) {
        return false;
      }

      return editableQuestion.contentBlock?.some((block) => !!block.image && !block.image.resourceKey.trim()) ?? false;
    },
  },

  DefaultValueRule: {
    key: 'defaultValue',
    text: 'Default option already exist',
    validate: (editableQuestion: ISurveyQuestion) => {
      return (editableQuestion?.options?.filter((o) => o.optionValue === o.defaultValue).length ?? 0) > 1;
    },
  },

  SingleChoiceAnswerRule: {
    key: 'answerValue',
    text: 'Only one answer can be choice for "Single choice question" type',
    validate: (editableQuestion: ISurveyQuestion) => {
      return (
        editableQuestion.questionType === QuestionType.SingleChose &&
        (editableQuestion?.options?.filter((o) => o.optionValue === o.answerValue).length ?? 0) > 1
      );
    },
  },

  YesNoOptionCountRule: {
    key: 'options',
    text: 'Yes / No question should have exactly 2 options',
    validate: (editableQuestion: ISurveyQuestion) => {
      return editableQuestion.questionType === QuestionType.YesNo && (editableQuestion?.options?.length ?? 0) !== 2;
    },
  },

  YesNoOptionValuesRule: {
    key: 'optionValue',
    text: 'Yes / No question should contain option values "true" and "false"',
    validate: (editableQuestion: ISurveyQuestion) => {
      if (editableQuestion.questionType !== QuestionType.YesNo) {
        return false;
      }

      const values = editableQuestion?.options?.map((o) => o.optionValue) ?? [];
      return !(values.includes('true') && values.includes('false'));
    },
  },

  DateInputOptionCountRule: {
    key: 'options',
    text: 'Date input question should have exactly 1 option',
    validate: (editableQuestion: ISurveyQuestion) => {
      return editableQuestion.questionType === QuestionType.DateInput && (editableQuestion?.options?.length ?? 0) !== 1;
    },
  },

  DateRangeOptionCountRule: {
    key: 'options',
    text: 'Date range question should have exactly 2 options',
    validate: (editableQuestion: ISurveyQuestion) => {
      return (
        editableQuestion.questionType === QuestionType.DateRangeInput && (editableQuestion?.options?.length ?? 0) !== 2
      );
    },
  },
};

export const getOptionValueError = (question: ISurveyQuestion | null, optionIndex: number): string | undefined => {
  if (!question?.options?.[optionIndex]) {
    return undefined;
  }

  return Object.values(ValidationRules).find(
    (rule) => rule.key === 'optionValue' && rule.validate(question, optionIndex),
  )?.text;
};
